import VisibleIcon from "@mui/icons-material/Visibility";
import HiddenIcon from "@mui/icons-material/VisibilityOff";
import { IconButton, InputAdornment, Paper, Stack, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import React, { ChangeEventHandler, useEffect, useState } from "react";

import {
    DragDropContext,
    Draggable,
    DraggableLocation,
    DraggableProvided,
    DraggableRubric,
    DraggableStateSnapshot,
    Droppable,
    DropResult,
    ResponderProvided
} from "react-beautiful-dnd";
import { DataView } from "../../DataView";
import SortIcon from "@mui/icons-material/ArrowUpward";
import ReverseSortIcon from "@mui/icons-material/ArrowDownward";
import * as api from "@crochik/pi-api";
import { FieldTypeIcon } from "../FormBody/FormCell/DragAndDrop/FieldTypeIcon";

interface IProps {
    dataView: DataView;
}

export function FieldsComponent(props: IProps) {
    const { dataView } = props;
    const { orderedFields } = dataView;
    const [fieldsList, setFieldsList] = useState<api.FormField[]>(orderedFields);
    const [showNames, setShowNames] = useState(false);
    const [search, setSearch] = useState<string>();

    useEffect(() => {
        updateFields();
    }, []);

    const updateFields = () => {
        console.log("updateFields");

        const hiddenFields = orderedFields.filter(x => !x.isVisible).sort((a, b) => (a.label ?? a.name ?? "")?.localeCompare((b.label ?? b.name ?? "")));
        const fields: api.FormField[] = [
            ...orderedFields.filter(x => x.isVisible),
            ...hiddenFields
        ];
        setFieldsList(fields);
    };

    const toggleVisibility = (x: api.FormField) => () => {
        if (!dataView || !x.name) return;
        dataView.toggleFieldVisibility(x.name);

        // this.forceUpdate();
        updateFields();
    };

    const reorder = (source: DraggableLocation, destination: DraggableLocation) => {
        const result = Array.from(fieldsList);
        const [removed] = result.splice(source.index, 1);
        result.splice(destination.index, 0, removed);

        const order = result.filter((x) => !!x.name).map((x) => x.name as string);
        console.log("new order", order);
        dataView.reoderFields(order);

        setFieldsList(result);
        // updateFields();
    };

    const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
        const { source, destination } = result;

        if (!destination) {
            console.log("drop outside");
            return;
        }

        console.log(`move ${source.index} to ${destination.index}`);
        reorder(source, destination);
        // this.forceUpdate();

        // updateFields();
    };

    const onSort = (x: api.FormField, reverse: boolean) => () => {
        dataView.sortByField(x.name!, reverse);
    };

    const renderChild = (x: api.FormField) => (provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: DraggableRubric) => {
        const style: React.CSSProperties = {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            userSelect: "none",
            justifyContent: "space-between",
            paddingLeft: 12,
            paddingTop: 4,
            paddingBottom: 4,
            // padding: 10,
            // border: '1px solid blue',
            // marginBottom: 10,
            background: snapshot.isDragging ? "yellow" : "white",
            ...provided.draggableProps.style
        };

        const isSortable = dataView.isSortable(x);
        const isSorted = isSortable && dataView.isSorted(x);
        const isSortedReverse = dataView.isSortReversed;

        return (
            <Paper key={x.name} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                style={style}>
                <div style={{ display: 'flex' }}>
                    <div style={{ width: 32 }}>
                        <FieldTypeIcon type={x.type ?? ""} />
                    </div>
                    <div>
                        <Typography>{x.label || x.name}</Typography>
                        {
                            showNames && !!x.label && x.label !== x.name &&
                            <>
                                <Typography color="gray" fontSize={10}>{x.name}</Typography>
                            </>
                        }
                        {
                            !!x.description &&
                            <>
                                <Typography fontSize={10}>{x.description}</Typography>
                            </>
                        }
                    </div>
                </div>
                <div>
                    {
                        isSortable && (
                            <>
                                <IconButton onClick={onSort(x, false)} size="small"><SortIcon color={isSorted && !isSortedReverse ? "primary" : undefined} /></IconButton>
                                <IconButton onClick={onSort(x, true)} size="small"><ReverseSortIcon color={isSorted && isSortedReverse ? "primary" : undefined} /></IconButton>
                            </>
                        )
                    }
                    <IconButton onClick={toggleVisibility(x)}>{x.name && dataView.isFieldVisible(x.name) ?
                        <VisibleIcon /> : <HiddenIcon />}</IconButton>
                </div>
            </Paper>
        );
    };

    const renderChildren = (shadow) => (provided, snapshot) => {
        // const { orderedFields } = dataView;
        // const fields = orderedFields;
        const style = { width: "100%", minHeight: "100px" }; //, backgroundColor: snapshot.isDraggingOver ? 'green' : 'white' }

        const fields = fieldsList;

        const searchUpper = search?.toUpperCase();
        return (
            <Stack spacing={1} ref={provided.innerRef} style={style} {...provided.droppableProps}>
                {fields.map((x, i) => {
                    let match = !searchUpper;
                    if (!!searchUpper && searchUpper.length > 0) {
                        if (!!x.name && x.name.toUpperCase().indexOf(searchUpper) >= 0) match = true;
                        if (!!x.label && x.label.toUpperCase().indexOf(searchUpper) >= 0) match = true;
                        if (!!x.description && x.description.toUpperCase().indexOf(searchUpper) >= 0) match = true;
                    }
                    if (!match) return null;
                    return (
                        <Draggable key={x.name} draggableId={x.name ?? i.toString()} index={i}>
                            {renderChild(x)}
                        </Draggable>
                    );
                })}
                {provided.placeholder}
            </Stack>
        );
    };

    const onSearch: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
        setSearch(e.target.value);
    };

    const onClearSearch = () => {
        setSearch(undefined);
    }

    return (
        <div>
            <div style={{ height: 40, position: 'relative' }}>
                <TextField value={search ?? ""} onChange={onSearch} sx={{ width: "100%" }} variant="standard" autoFocus
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconButton onClick={onClearSearch} sx={{ padding: "2px" }}>
                                    <SearchIcon/>
                                </IconButton>
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                {!!search && search?.length > 0 && (
                                    <IconButton onClick={onClearSearch} sx={{ padding: "2px" }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </InputAdornment>
                        ),
                    }}
                />
            </div>
            <div
                style={{
                    position: 'relative',
                    maxHeight: "530px",
                    overflow: "auto",
                    paddingBottom: 12,
                }}
            >
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable
                        key="container"
                        droppableId="container"
                    // isDropDisabled={true}
                    // renderClone={this.cloneItem(this.tools)}
                    >
                        {renderChildren(false)}
                    </Droppable>
                </DragDropContext>
            </div>
        </div >
    );
}
